import { Bell, CheckCircle2, Flame, Thermometer, WifiOff, Wind, XCircle } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAlertContext } from "@/shared/api/hooks/alerts";
import { useRiskSamples } from "@/shared/api/hooks/risk";
import { alertTone, type Level } from "@/shared/lib/levelTone";
import { Badge } from "@/shared/ui/Badge";
import { Button } from "@/shared/ui/Button";
import { Card, CardBody } from "@/shared/ui/Card";
import { EmptyState } from "@/shared/ui/EmptyState";
import { Separator } from "@/shared/ui/Separator";
import { Skeleton } from "@/shared/ui/Skeleton";
import { StatusDot } from "@/shared/ui/StatusDot";
import { STATE_LABEL_KEY, stateBadge } from "./alertMeta";
import {
  alertDurationS, alertTitle, clearedByName, clearedHow, clearReason, formatDuration, gasName, isComm,
  kindLabel, plainMessage, riAtRaise, shortTime, spanText, whereLine,
} from "./alertText";
import { AckTargetNote } from "./AckTargetBadge";
import { IncidentReport } from "./IncidentReport";
import { RiskBar } from "./RiskBar";
import type { Alert, AlertContext, AlertState } from "@/shared/api/models";

/**
 * DetailPanel — 경보 1건의 상세. 경보 페이지(오른쪽 열·폰 전체 화면)와 실경보 오버레이가 같이 쓴다.
 *
 * ## 2026-09-24 개편 — 「클릭 시 상세에서도 상세내용으로 보이게」
 * 예전 상세는 시각 세 줄과 id 숫자(확인자)뿐이었다. 이제 한 화면에:
 *   · 무엇이·어디서·어떻게 — 사람 말 제목, 현장·함체, 본문 전문
 *   · 흐름 — 발생 → 확인(누가) → 해제(자동/누가), 이어진 시간
 *   · 위험도 막대 — 발령 당시 vs 지금, 가스별 기여
 *   · 당시 환경 — 발령 직전 그 함체의 전 채널 값·온습도·바람(알림 본문과 같은 스냅샷)
 *   · 통신 이상이면 — 데이터가 끊긴 시각·다시 들어온 시각, 같은 사건으로 묶인 경보들
 *   · 알림 발송 — 푸시·메일·문자가 실제로 나갔는지
 * 맥락(`/alerts/{id}/context/`)을 못 받아도(옛 서버) 나머지는 그대로 보인다.
 */
export function DetailPanel({
  alert,
  onAck,
  onClear,
  canAck = true,
  canClear = true,
  onOpenAlert,
}: {
  alert: Alert | null;
  onAck: (id: number) => void;
  onClear: (id: number) => void;
  /** RBAC — hide (not disable) the control when the user can't perform it. */
  canAck?: boolean;
  canClear?: boolean;
  /** 같은 사건의 다른 경보를 눌렀을 때. 없으면 목록만 보여 준다. */
  onOpenAlert?: (id: number) => void;
}) {
  const { t } = useTranslation();
  const ctx = useAlertContext(alert?.id ?? null);
  if (!alert) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            Icon={Bell}
            title={t("widgets.alerts.detailPanel.emptyTitle")}
            description={t("widgets.alerts.detailPanel.emptyDesc")}
          />
        </CardBody>
      </Card>
    );
  }
  const comm = isComm(alert);
  const tone = alertTone(alert, comm);   // 설비 무응답 = 회색 「설비」(등급색 아님)
  const Icon = comm ? WifiOff : Flame;
  const isOpen = alert.state === "open";
  const isCleared = alert.state === "cleared";
  const body = plainMessage(t, alert);
  const where = whereLine(t, alert);

  return (
    <Card>
      <CardBody className="space-y-4">
        {/* ── 무엇이 · 어디서 ── */}
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px]"
            style={{ background: `var(${tone.soft})`, color: `var(${tone.fg})` }}
          >
            <Icon size={20} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[18px] font-semibold leading-snug tracking-[-0.012em] break-keep">
              {alertTitle(t, alert)}
            </h2>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span
                className="rounded-[6px] px-1.5 py-[1px] text-[12px] font-semibold"
                style={{ background: `var(${tone.soft})`, color: `var(${tone.fg})` }}
              >
                {t(tone.labelKey)}
              </span>
              <Badge tone={stateBadge(alert.state as AlertState)}>
                {t(STATE_LABEL_KEY[alert.state as AlertState])}
              </Badge>
              <span className="text-[12px] text-[var(--fg-subtle)]">{kindLabel(t, alert)}</span>
              {alert.is_drill && <Badge tone="warning">{t("widgets.alerts.feedCard.drill")}</Badge>}
            </div>
          </div>
        </div>

        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
          <dt className="text-[var(--fg-subtle)]">{t("widgets.alerts.v2.field.site")}</dt>
          <dd className="font-medium break-keep">{alert.site_name || "—"}</dd>
          <dt className="text-[var(--fg-subtle)]">
            {comm ? t("widgets.alerts.v2.field.device") : t("widgets.alerts.v2.field.sensor")}
          </dt>
          <dd className="font-medium break-keep">
            {comm ? where.split(" · ").slice(1).join(" · ") || "—" : nodeText(alert)}
          </dd>
          {body && (
            <>
              <dt className="text-[var(--fg-subtle)]">{t("widgets.alerts.v2.field.what")}</dt>
              <dd className="break-keep whitespace-pre-line">{body}</dd>
            </>
          )}
        </dl>

        {/* 확인·해제는 **위에** 둔다 — 상세가 길어져(측정값·흐름·발송) 맨 아래 버튼이 화면 밖으로
            밀렸다(넓은 화면 오른쪽 열·실경보 오버레이, 2026-09-24 리뷰). */}
        {(canAck || canClear) && !isCleared && (
          <div className="flex flex-wrap items-center gap-2">
            {canAck && (
              <Button intent="primary" size="md" disabled={!isOpen} onClick={() => onAck(alert.id!)}
                      leading={<CheckCircle2 size={15} strokeWidth={1.85} />}>
                {isOpen ? t("widgets.alerts.detailPanel.ack") : t("widgets.alerts.v2.acked")}
              </Button>
            )}
            {canClear && (
              <Button intent="outline" size="md" onClick={() => onClear(alert.id!)}
                      leading={<XCircle size={15} strokeWidth={1.85} />}>
                {t("widgets.alerts.detailPanel.clear")}
              </Button>
            )}
            {comm && (
              <p className="basis-full text-[12px] leading-snug text-[var(--fg-subtle)] break-keep">
                {t("widgets.alerts.v2.commClearHint")}
              </p>
            )}
          </div>
        )}

        <Separator />

        {/* ── 흐름 ── */}
        <Timeline alert={alert} />

        {/* ── 위험도 / 통신 구간 ── */}
        {/* 맥락 API 가 없는 옛 서버(3.0.24 이하)면 그 칸을 통째로 숨긴다 — 「아직 안 들어옴」 같은 거짓 값을 그리지 않는다. */}
        {comm ? (
          !ctx.isError && (
            <OutageSection alert={alert} ctx={ctx.data} loading={ctx.isLoading} onOpenAlert={onOpenAlert} />
          )
        ) : (
          <RiskSection alert={alert} ctx={ctx.data} />
        )}

        {/* ── 당시 환경 ── */}
        {!comm && !ctx.isError && <EnvSection alert={alert} ctx={ctx.data} loading={ctx.isLoading} />}

        <Separator />

        <DeliverySection delivery={alert.delivery} />

      </CardBody>
    </Card>
  );
}

function nodeText(a: Alert): string {
  const code = (a.node_code ?? "").trim();
  const label = (a.node_label ?? "").trim();
  if (code && label && label !== code) return `${code} · ${label}`;
  return code || label || "—";
}

/* ─── 흐름: 발생 → 확인 → 해제 ────────────────────────────────────────── */

function Timeline({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const how = clearedHow(alert);
  const who = clearedByName(alert);
  const reason = clearReason(alert);
  const clearedNote = [how === "person" && who ? who : "", reason ? t("widgets.alerts.v2.clearReason", { reason }) : ""]
    .filter(Boolean)
    .join(" · ");
  const steps: { key: string; label: string; at: string | null | undefined; note?: string | undefined; done: boolean }[] = [
    { key: "raised", label: t("widgets.alerts.v2.step.raised"), at: alert.raised_at, done: true },
    {
      key: "ack",
      label: t("widgets.alerts.v2.step.acked"),
      at: alert.acknowledged_at,
      note: alert.acknowledged_by_name || undefined,
      done: !!alert.acknowledged_at,
    },
    {
      key: "cleared",
      label: how === "auto" ? t("widgets.alerts.v2.step.clearedAuto") : t("widgets.alerts.v2.step.cleared"),
      at: alert.cleared_at,
      note: clearedNote || undefined,
      done: !!alert.cleared_at,
    },
  ];
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-[var(--fg-muted)]">{t("widgets.alerts.v2.flow")}</span>
        <span className="text-[12px] tabular-nums text-[var(--fg-subtle)]">
          {alert.cleared_at
            ? t("widgets.alerts.v2.lasted", { dur: formatDuration(t, alertDurationS(alert)) })
            : t("widgets.alerts.v2.meta.ongoing", { dur: formatDuration(t, alertDurationS(alert)) })}
        </span>
      </div>
      <ol className="grid grid-cols-3 gap-2">
        {steps.map((s) => (
          <li
            key={s.key}
            className={
              "min-w-0 rounded-[10px] border px-2.5 py-2 " +
              (s.done ? "border-[var(--border)] bg-[var(--bg-subtle)]" : "border-dashed border-[var(--border)]")
            }
          >
            <div className="text-[11.5px] text-[var(--fg-subtle)] break-keep">{s.label}</div>
            <div className="mt-0.5 text-[13px] font-semibold tabular-nums">
              {s.at ? shortTime(s.at, false) : "—"}
            </div>
            {s.at && (
              <div className="text-[11px] tabular-nums text-[var(--fg-subtle)]">{shortTime(s.at).slice(0, 5)}</div>
            )}
            {s.note && <div className="mt-0.5 text-[11.5px] text-[var(--fg-muted)] break-all">{s.note}</div>}
            {s.key === "ack" && <AckTargetNote alert={alert} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ─── 가스: 위험도 막대 ────────────────────────────────────────────── */

function RiskSection({ alert, ctx }: { alert: Alert; ctx: AlertContext | undefined }) {
  const { t } = useTranslation();
  const lv = (alert.level ?? 0) as Level;
  // 지금 이 함체의 위험도 — 같은 현장 표본 질의(관제 화면과 같은 캐시)에서 최신 1건.
  const samples = useRiskSamples(alert.site ?? undefined);
  const nowRi = useMemo(() => {
    for (const s of samples.data?.results ?? []) {
      if (s.node_id === alert.node) return s.ri_dynamic ?? s.ri_instant ?? null;
    }
    return null;
  }, [samples.data, alert.node]);
  // 현장 PC 가 올린 경보는 ri_at_open 이 가스 측정값이다 — 엔진 표본(ctx.risk)이나 엔진 경보만 RI 로 쓴다.
  const atRaise = ctx?.risk?.ri ?? riAtRaise(alert);
  const contrib = Object.entries(ctx?.risk?.contributors ?? {})
    .filter(([, v]) => v > 0.005)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  return (
    <div className="space-y-3">
      <div className="text-[12.5px] font-semibold text-[var(--fg-muted)]">{t("widgets.alerts.v2.riskTitle")}</div>
      {atRaise != null && (
        <RiskBar size="md" value={atRaise} level={lv} label={t("widgets.alerts.v2.riskAtRaise")} />
      )}
      {alert.state !== "cleared" && (
        <RiskBar
          size="md"
          value={nowRi}
          label={t("widgets.alerts.v2.riskNow")}
          compare={atRaise}
          compareLabel={atRaise != null ? t("widgets.alerts.v2.riskAtRaiseShort") : undefined}
        />
      )}
      {contrib.length > 0 && (
        <div>
          <div className="mb-1 text-[12px] text-[var(--fg-subtle)]">{t("widgets.alerts.v2.contribTitle")}</div>
          <ul className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5">
            {contrib.map(([gas, v]) => (
              <li key={gas} className="contents">
                <span className="max-w-[45vw] text-[12.5px] break-keep [overflow-wrap:anywhere]">{gasName(t, gas)}</span>
                <RiskBar value={v} />
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[11.5px] leading-snug text-[var(--fg-subtle)] break-keep">{t("widgets.alerts.v2.riskHint")}</p>
    </div>
  );
}

/* ─── 가스: 당시 환경 ─────────────────────────────────────────────── */

function EnvSection({ alert, ctx, loading }: { alert: Alert; ctx: AlertContext | undefined; loading: boolean }) {
  const { t } = useTranslation();
  const snap = ctx?.snapshot;
  if (loading) return <Skeleton className="h-24 w-full" />;
  if (!snap) {
    return (
      <p className="text-[12.5px] text-[var(--fg-subtle)]">{t("widgets.alerts.v2.envNone")}</p>
    );
  }
  const main = (alert.gas ?? "").toUpperCase();
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] font-semibold text-[var(--fg-muted)]">{t("widgets.alerts.v2.envTitle")}</span>
        <span className="text-[11.5px] tabular-nums text-[var(--fg-subtle)]">
          {t("widgets.alerts.v2.envAt", { at: shortTime(alert.raised_at) })}
        </span>
      </div>
      <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {snap.channels.map((c) => {
          const hit = c.gas === main;
          return (
            <li
              key={c.gas}
              className={
                "min-w-0 rounded-[10px] border px-2.5 py-2 " +
                (hit ? "border-[var(--warning)] bg-[var(--warning-soft)]" : "border-[var(--border)]")
              }
            >
              <div className={"text-[11.5px] break-keep " + (hit ? "text-[var(--warning-fg)] font-semibold" : "text-[var(--fg-subtle)]")}>
                {gasName(t, c.gas)}
              </div>
              <div className={"text-[16px] font-semibold tabular-nums " + (hit ? "text-[var(--warning-fg)]" : "")}>
                {fmtValue(c.value)} <span className="text-[11.5px] font-medium">{c.unit || "ppm"}</span>
              </div>
            </li>
          );
        })}
      </ul>
      {(snap.temperature_c != null || snap.humidity_pct != null || snap.wind_dir_deg != null) && (
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-[var(--fg-muted)]">
          {snap.temperature_c != null && (
            <span className="inline-flex items-center gap-1">
              <Thermometer size={13} aria-hidden /> {snap.temperature_c.toFixed(1)}℃
              {snap.humidity_pct != null && ` · ${Math.round(snap.humidity_pct)}%`}
            </span>
          )}
          {snap.wind_dir_deg != null && (
            <span className="inline-flex items-center gap-1">
              <Wind size={13} aria-hidden /> {Math.round(snap.wind_dir_deg)}°
              {snap.wind_speed_mps != null && ` · ${snap.wind_speed_mps.toFixed(1)} m/s`}
            </span>
          )}
        </div>
      )}
      {ctx?.reference && (
        <p className="mt-2 text-[12px] text-[var(--fg-subtle)] break-keep">
          {t("widgets.alerts.v2.reference", { text: ctx.reference })}
        </p>
      )}
    </div>
  );
}

function fmtValue(v: number): string {
  return Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1);
}

/* ─── 통신 이상: 끊긴 구간 · 같은 사건 ─────────────────────────────── */

function OutageSection({
  alert, ctx, loading, onOpenAlert,
}: {
  alert: Alert;
  ctx: AlertContext | undefined;
  loading: boolean;
  onOpenAlert?: ((id: number) => void) | undefined;
}) {
  const { t } = useTranslation();
  if (loading) return <Skeleton className="h-20 w-full" />;
  const out = ctx?.outage;
  const ep = ctx?.episode;
  return (
    <div className="space-y-3">
      <div className="text-[12.5px] font-semibold text-[var(--fg-muted)]">{t("widgets.alerts.v2.outageTitle")}</div>
      <div className="grid grid-cols-2 gap-2">
        <Stat label={t("widgets.alerts.v2.outage.lastData")} value={shortTime(out?.last_data_before)} />
        <Stat
          label={t("widgets.alerts.v2.outage.backAt")}
          value={out?.first_data_after ? shortTime(out.first_data_after) : t("widgets.alerts.v2.outage.notYet")}
        />
      </div>
      {out?.gap_s != null && (
        <p className="text-[13px] font-medium">
          {t("widgets.alerts.v2.outage.gap", { dur: formatDuration(t, out.gap_s) })}
        </p>
      )}
      {ep && ep.alert_count > 1 && (
        <div>
          <div className="mb-1 text-[12px] text-[var(--fg-subtle)] break-keep">
            {t("widgets.alerts.v2.episodeLine", {
              span: spanText(ep.down_since ?? ep.detected_at, ep.ended_at),
              dur: formatDuration(t, ep.duration_s),
              n: ep.alert_count,
            })}
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {ep.alert_ids.map((id) => (
              <li key={id}>
                <button
                  type="button"
                  disabled={!onOpenAlert || id === alert.id}
                  onClick={() => onOpenAlert?.(id)}
                  className={
                    "rounded-[8px] border px-2 py-1 text-[12px] tabular-nums " +
                    (id === alert.id
                      ? "border-[var(--accent)] text-[var(--accent)] font-semibold"
                      : "border-[var(--border)] text-[var(--fg-muted)] hover:bg-[var(--bg-hover)]")
                  }
                >
                  #{id}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {ctx?.incident && (
        <div>
          <div className="mb-1 text-[12px] text-[var(--fg-subtle)]">{t("widgets.alerts.v2.incident.title")}</div>
          <IncidentReport inc={ctx.incident} />
        </div>
      )}
      <p className="text-[11.5px] leading-snug text-[var(--fg-subtle)] break-keep">{t("widgets.alerts.v2.outageHint")}</p>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-[10px] border border-[var(--border)] px-2.5 py-2">
      <div className="text-[11.5px] text-[var(--fg-subtle)] break-keep">{label}</div>
      <div className="mt-0.5 text-[14px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

/* ─── 알림 발송 ────────────────────────────────────────────────────── */

// `label` holds an i18n key; resolved with t() at the render site.
const DELIVERY_CHANNELS: { key: string; label: string; onlyIfRecorded?: boolean }[] = [
  { key: "in_app", label: "widgets.alerts.detailPanel.channel.in_app" },
  { key: "push", label: "widgets.alerts.detailPanel.channel.push" },
  { key: "email", label: "widgets.alerts.v2.channel.email" },
  { key: "sms", label: "widgets.alerts.detailPanel.channel.sms" },
  { key: "relay", label: "widgets.alerts.detailPanel.channel.relay" },
  // 119 — 위험 경보에서만 기록된다(게이트를 열었거나, 「119 신고 대응 미사용(설정)」으로 건너뜀). 없으면 줄을 안 그린다.
  { key: "emergency", label: "widgets.alerts.detailPanel.channel.emergency", onlyIfRecorded: true },
];
const DELIVERY_STATUS: Record<
  string,
  { label: string; dot: "ok" | "caution" | "offline" | "critical" }
> = {
  sent: { label: "widgets.alerts.detailPanel.delivery.sent", dot: "ok" },
  skipped: { label: "widgets.alerts.detailPanel.delivery.skipped", dot: "caution" },
  unconfigured: { label: "widgets.alerts.detailPanel.delivery.unconfigured", dot: "offline" },
  failed: { label: "widgets.alerts.detailPanel.delivery.failed", dot: "critical" },
};

/** Per-channel alert fan-out status, read off `Alert.delivery`. 글자를 자르지 않는다. */
function DeliverySection({ delivery }: { delivery: unknown }) {
  const { t } = useTranslation();
  const d = (delivery ?? {}) as Record<string, { status?: string; detail?: string }>;
  return (
    <div>
      <div className="mb-1.5 text-[12.5px] font-semibold text-[var(--fg-muted)]">
        {t("widgets.alerts.detailPanel.deliveryTitle")}
      </div>
      <ul className="space-y-1.5">
        {DELIVERY_CHANNELS.filter((ch) => !ch.onlyIfRecorded || d[ch.key]).map((ch) => {
          const rec = d[ch.key];
          const st = rec?.status ? DELIVERY_STATUS[rec.status] : null;
          return (
            <li key={ch.key} className="flex items-start gap-2 text-[12.5px]">
              <StatusDot kind={st?.dot ?? "offline"} size={7} className="mt-[5px]" />
              <span className="w-[64px] shrink-0 text-[var(--fg)] break-keep [overflow-wrap:anywhere]">{t(ch.label)}</span>
              <span className="min-w-0 flex-1 text-[var(--fg-subtle)] break-keep">
                {st ? t(st.label) : "—"}
                {rec?.detail && <span className="text-[12px]"> · {rec.detail}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
